// trace input/output 中 LangChain / OpenAI 内容块的解析，用于消息气泡与列表预览。

/** 超过该字符数的内嵌图片不自动渲染 */
export const LARGE_IMAGE_CHARS = 2_800_000

export type MediaPart =
  | {
      kind: 'image'
      key: string
      mime: string
      name: string
      size?: number
      /** 内嵌 base64（不含 data: 前缀） */
      base64?: string
      /** data: 或 http(s) 地址 */
      url?: string
      /** 附件原始地址（extras.attachment.source_url） */
      sourceUrl?: string
    }
  | { kind: 'file'; key: string; mime: string; name: string; size?: number; url?: string }
  | { kind: 'media-ref'; key: string; mime: string; id: string }
  | { kind: 'unknown'; key: string; type: string; raw: unknown }

export interface ParsedContent {
  text: string
  thinking: string
  parts: MediaPart[]
}

type Block = Record<string, unknown>

const MEDIA_REF = /@@@langfuseMedia:type=([^|@]+)\|id=([^|@]+)\|[^@]*@@@/g
const DATA_URI = /^data:([\w.+-]+\/[\w.+-]+)?(?:;[^,]*)?;base64,/

const str = (v: unknown) => (typeof v === 'string' ? v : '')

/** extras.attachment 可能是对象，也可能是 Python repr 字符串 */
function attachmentMeta(extras: unknown): { path: string; sourceUrl: string; size?: number } {
  if (extras && typeof extras === 'object') {
    const a = ((extras as Block).attachment ?? {}) as Block
    const size = Number(a.content_length)
    return {
      path: str(a.path),
      sourceUrl: str(a.source_url),
      size: Number.isFinite(size) && size > 0 ? size : undefined,
    }
  }
  const s = str(extras)
  const pick = (k: string) => s.match(new RegExp(`'${k}':\\s*'([^']*)'`))?.[1] ?? ''
  const size = Number(s.match(/'content_length':\s*(\d+)/)?.[1])
  return { path: pick('path'), sourceUrl: pick('source_url'), size: size > 0 ? size : undefined }
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

const baseName = (p: string) => safeDecode(p.split('?')[0].split('/').filter(Boolean).pop() ?? '')
const base64Bytes = (b64: string) => Math.floor((b64.length * 3) / 4)

function mediaRefs(s: string): MediaPart[] {
  return [...s.matchAll(MEDIA_REF)].map(m => ({ kind: 'media-ref' as const, key: `ref:${m[2]}`, mime: m[1], id: m[2] }))
}

/** 解析单个内容块；返回 null 表示不是多模态块 */
function parseMediaBlock(block: Block, index: number): MediaPart | null {
  const type = str(block.type)
  const mime = str(block.mime_type) || str(block.media_type)
  const meta = attachmentMeta(block.extras)
  if (type === 'image' || type === 'input_image') {
    const b64 = str(block.base64) || str(block.data)
    const url = str(block.url) || str(block.image_url)
    const refs = mediaRefs(url)
    if (refs.length) return refs[0]
    const dataMime = url.match(DATA_URI)?.[1]
    return {
      kind: 'image',
      key: meta.sourceUrl || meta.path || (url && !dataMime ? url : `image:${index}:${(b64 || url).length}`),
      mime: mime || dataMime || 'image/*',
      name: baseName(meta.path || meta.sourceUrl || (dataMime ? '' : url)) || '图片',
      size: meta.size ?? (b64 ? base64Bytes(b64) : undefined),
      base64: b64 || undefined,
      url: url || undefined,
      sourceUrl: meta.sourceUrl || undefined,
    }
  }
  if (type === 'image_url') {
    const raw = block.image_url
    const url = typeof raw === 'string' ? raw : str((raw as Block | undefined)?.url)
    const refs = mediaRefs(url)
    if (refs.length) return refs[0]
    const dataMime = url.match(DATA_URI)?.[1]
    return {
      kind: 'image',
      key: dataMime ? `image:${index}:${url.length}` : url,
      mime: dataMime || 'image/*',
      name: dataMime ? '图片' : baseName(url) || '图片',
      size: dataMime ? base64Bytes(url) : undefined,
      url,
    }
  }
  if (['file', 'input_file', 'audio', 'input_audio', 'video', 'document'].includes(type)) {
    const url = str(block.url)
    const b64 = str(block.base64) || str(block.data)
    return {
      kind: 'file',
      key: meta.sourceUrl || meta.path || url || `file:${index}:${b64.length}`,
      mime: mime || 'application/octet-stream',
      name: str(block.filename) || baseName(meta.path || meta.sourceUrl || url) || '文件',
      size: meta.size ?? (b64 ? base64Bytes(b64) : undefined),
      url: url || undefined,
    }
  }
  return null
}

/** 把消息 content 拆成文本、推理内容与多模态附件 */
export function parseContent(raw: unknown): ParsedContent {
  if (raw == null) return { text: '', thinking: '', parts: [] }
  if (typeof raw === 'string') return parseString(raw)
  if (!Array.isArray(raw)) {
    return { text: safeStringify(raw, 2), thinking: '', parts: [] }
  }
  const texts: string[] = []
  const thinkings: string[] = []
  const parts: MediaPart[] = []
  raw.forEach((block, index) => {
    if (block == null) return
    if (typeof block === 'string') {
      const p = parseString(block)
      if (p.text) texts.push(p.text)
      parts.push(...p.parts)
      return
    }
    if (typeof block !== 'object') return
    const b = block as Block
    const type = str(b.type)
    if (type === 'text') {
      const p = parseString(str(b.text))
      if (p.text) texts.push(p.text)
      parts.push(...p.parts)
    } else if (type === 'thinking' || type === 'reasoning') {
      const t = str(b.thinking) || str(b.reasoning)
      if (t) thinkings.push(t)
    } else if (type === 'tool_use' || type === 'redacted_thinking') {
      // tool_use 已在 tool_calls 中展示；redacted_thinking 无可读内容
    } else {
      parts.push(parseMediaBlock(b, index) ?? { kind: 'unknown', key: `unknown:${index}`, type: type || 'object', raw: b })
    }
  })
  return { text: texts.join('\n'), thinking: thinkings.join('\n\n'), parts }
}

function parseString(s: string): ParsedContent {
  const trimmed = s.trim()
  const dataMime = trimmed.match(DATA_URI)?.[1]
  if (dataMime?.startsWith('image/')) {
    return {
      text: '',
      thinking: '',
      parts: [{ kind: 'image', key: `image:${trimmed.length}`, mime: dataMime, name: '图片', size: base64Bytes(trimmed), url: trimmed }],
    }
  }
  const parts = mediaRefs(s)
  return { text: parts.length ? s.replace(MEDIA_REF, '').trim() : s, thinking: '', parts }
}

/** 按 key 去重；同一图片优先保留内嵌 base64 的一份 */
export function uniqueParts(parts: MediaPart[]): MediaPart[] {
  const byKey = new Map<string, MediaPart>()
  for (const p of parts) {
    const prev = byKey.get(p.key)
    if (!prev || (p.kind === 'image' && p.base64 && !(prev.kind === 'image' && prev.base64))) byKey.set(p.key, p)
  }
  return [...byKey.values()]
}

/** 附件的单行文字描述，用于列表预览 */
export function partLabel(p: MediaPart): string {
  switch (p.kind) {
    case 'image':
    case 'media-ref':
      return '[图片]'
    case 'file':
      return `[文件: ${p.name}]`
    default:
      return `[${p.type}]`
  }
}

export function formatBytes(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`
}

/** 深拷贝并截断长字符串，返回是否发生截断 */
export function truncateLongStrings(value: unknown, max = 1000): { value: unknown; truncated: boolean } {
  let truncated = false
  const walk = (v: unknown, depth: number): unknown => {
    if (typeof v === 'string') {
      if (v.length <= max) return v
      truncated = true
      return `${v.slice(0, 100)}…[已省略 ${(v.length - 100).toLocaleString()} 字符]`
    }
    if (depth > 50 || v === null || typeof v !== 'object') return v
    if (Array.isArray(v)) return v.map(x => walk(x, depth + 1))
    return Object.fromEntries(Object.entries(v as Block).map(([k, x]) => [k, walk(x, depth + 1)]))
  }
  return { value: walk(value, 0), truncated }
}

/** JSON.stringify，长字符串截断 */
export function safeStringify(value: unknown, space?: number, max = 1000): string {
  return JSON.stringify(truncateLongStrings(value, max).value, null, space) ?? ''
}

const MIME_LABELS: [RegExp, string][] = [
  [/pdf/, 'PDF'],
  [/wordprocessingml|msword/, 'Word'],
  [/spreadsheetml|ms-excel/, 'Excel'],
  [/presentationml|ms-powerpoint/, 'PPT'],
  [/csv/, 'CSV'],
  [/json/, 'JSON'],
  [/^text\//, 'TXT'],
  [/^audio\//, '音频'],
  [/^video\//, '视频'],
]

/** 文件类型的简短标签 */
export function mimeLabel(mime: string): string {
  if (mime.startsWith('image/')) return mime.slice(6).toUpperCase()
  return MIME_LABELS.find(([re]) => re.test(mime))?.[1] ?? '文件'
}

/** 文件类型标签，优先用扩展名（如 .md 的 mime 是 text/plain） */
export function fileLabel(name: string, mime: string): string {
  const ext = name.includes('.') ? name.split('.').pop() ?? '' : ''
  return /^[a-z0-9]{1,5}$/i.test(ext) ? ext.toUpperCase() : mimeLabel(mime)
}

/** 图片能否在浏览器中直接显示（内嵌 base64、data: 或 https 地址） */
export function imageSrc(part: Extract<MediaPart, { kind: 'image' }>): string | undefined {
  if (part.base64) return `data:${part.mime.includes('*') ? 'image/png' : part.mime};base64,${part.base64}`
  if (part.url && (part.url.startsWith('data:') || part.url.startsWith('https://'))) return part.url
  return undefined
}
